import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { groupBy, uniqBy } from "lodash-es";
import { useUserDetail } from "@/api/use-user-detail";
import { useUsersUpdates } from "@/api/use-users-updates";
import Avatar from "@/components/ui/avatar/avatar";
import { AvatarGroup } from "@/components/ui/avatar/avatar-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  BwCheckmarkLine,
  BwChevronDownLine,
  BwUserLine,
} from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { getDefaultAccount } from "@/libs/account";
import { UserRole } from "@/types/openapi-enum";

type UserUpdatesSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: string[];
  onChange: (value: string[]) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  placeId: string;
  assigneeId: string;
  placeholder?: string;
  "data-test"?: string;
};

export const UserUpdatesSelect = ({
  className,
  defaultOpen = false,
  value,
  onChange,
  onOpenChange,
  modal = false,
  placeId,
  disabled,
  assigneeId,
  placeholder,
  ...props
}: UserUpdatesSelectProps) => {
  const [open, toggleOpen] = useToggle(defaultOpen);
  const { data: users = [], isLoading } = useUsersUpdates(placeId);
  const { data: assignee } = useUserDetail(assigneeId);

  const allUsers = assignee ? uniqBy([assignee, ...users], "id") : users;

  // Resolve the selected ids back to full user objects (for the avatar trigger).
  const selectedUsers = value
    .map((id) => allUsers.find((user: any) => user.id === id))
    .filter(Boolean) as any[];

  const optionGroups = groupBy(allUsers, (user: any) => {
    const account = getDefaultAccount(user?.accounts || []);
    if (
      [UserRole.Member, UserRole.Owner].includes(
        user.defaultRole as UserRole,
      ) &&
      ((user.roles && user.roles.length === 1) || user.roles === null)
    ) {
      return "Member";
    }
    return account?.name || "Other";
  });

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    toggleOpen(open);
  };

  const toggleUser = (id: string) => {
    const newValue = value.includes(id)
      ? value.filter((entry) => entry !== id)
      : [...value, id];
    onChange(newValue);
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger className="group" asChild>
        <div
          data-test="order-update-trigger"
          role="combobox"
          className={cn(
            /* sorted */
            "flex",
            "grow items-center",
            "min-h-10",
            "px-3 py-2",
            "rounded-lg cursor-pointer",
            "transition-[color,box-shadow]",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-[1.5px] data-[state=open]:outline-outline-dark-max",
            value.length === 0 && "text-text-dark-min",
            disabled && "pointer-events-none opacity-50",
            className,
          )}
          {...props}
        >
          {selectedUsers.length > 0 ? (
            <div className="flex items-center flex-wrap gap-2 grow">
              {selectedUsers.length > 1 ? (
                <>
                  <AvatarGroup
                    data={selectedUsers
                      .map((user) => ({
                        src: user?.avatarUrl || "",
                        name: user?.name || "",
                      }))
                      .sort((a, b) => a.name.localeCompare(b.name))}
                    showMore={false}
                    size="sm"
                    labelClassName="text-sm text-text-dark-max"
                  />
                  <p className="text-sm text-text-dark-max line-clamp-1 grow">
                    {selectedUsers.length} members
                  </p>
                </>
              ) : (
                <>
                  <Avatar
                    src={selectedUsers[0]?.avatarUrl || ""}
                    name={selectedUsers[0]?.name}
                    size="sm"
                  />
                  <p
                    className="text-sm text-text-dark-max line-clamp-1 grow"
                    title={selectedUsers[0]?.name}
                  >
                    {selectedUsers[0]?.name}
                  </p>
                </>
              )}
              <BwChevronDownLine
                size={16}
                className="text-text-dark-high shrink-0"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 grow">
              <div className="flex items-center justify-center shrink-0 w-5 h-5 overflow-hidden border border-dashed rounded-full border-border-dark-high">
                <BwUserLine className="text-text-dark-min size-4" />
              </div>
              <p className="text-sm text-text-dark-min line-clamp-1 grow">
                {placeholder ?? "Not provided"}
              </p>
              <BwChevronDownLine
                size={16}
                className="text-text-dark-high shrink-0"
              />
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            {isLoading ? (
              <UserUpdatesSelectListSkeleton />
            ) : (
              <>
                <CommandEmpty>No user found.</CommandEmpty>

                {Object.entries(optionGroups)
                  ?.sort(([a], [b]) => {
                    // Ensure "Member" group always appears first
                    if (a === "Member") return -1;
                    if (b === "Member") return 1;
                    if (a === "Other") return 1;
                    if (b === "Other") return -1;
                    return a.localeCompare(b);
                  })
                  ?.map(([groupKey, options]) => (
                    <CommandGroup heading={groupKey} key={groupKey}>
                      {options.map((option: any) => (
                        <CommandItem
                          value={option.name}
                          key={option.id}
                          onSelect={() => toggleUser(option.id)}
                        >
                          <Avatar
                            src={option.avatarUrl || ""}
                            name={option.name}
                            size="sm"
                          />
                          <p className="grow line-clamp-1">{option.name}</p>
                          <BwCheckmarkLine
                            size={16}
                            className={cn(
                              /* sorted */
                              "shrink-0",
                              value.includes(option.id)
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const UserUpdatesSelectListSkeleton = () => (
  <div className="flex flex-col gap-0.5 p-1">
    {Array.from({ length: 7 }).map((_, item) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: fixed 7-row skeleton placeholder, never reorders, no identity
      <Skeleton key={item} className="h-8" />
    ))}
  </div>
);
